// src/admin/projects/ProjectForm.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import useAdminStore from "../store/useAdminStore";
import styles from "./ProjectsAdmin.module.css";

export default function ProjectForm({ mode = "new" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects, addProject, updateProject } = useAdminStore();
  const project = projects.find(p => p.id.toString() === id);

  const [form, setForm] = useState({
    title: "",
    shortDesc: "",
    description: "",
    img: "",
    images: [""],
    languages: [],
    github: "",
    demo: "",
    video: ""
  });

  useEffect(() => {
    if (mode === "edit" && project) {
      setForm({
        title: project.title || "",
        shortDesc: project.shortDesc || "",
        description: project.description || "",
        img: project.img || "",
        images: project.images || [""],
        languages: project.languages || [],
        github: project.github || "",
        demo: project.demo || "",
        video: project.video || ""
      });
    }
  }, [mode, project]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "new") {
      addProject({ ...form, id: Date.now() });
    } else {
      updateProject(id, form);
    }
    navigate("/admin/projects");
  };

  const addImageField = () => setForm({ ...form, images: [...form.images, ""] });
  const removeImageField = (i) => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) });

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.backBtn}>
        <ArrowLeft size={22} />
        Volver
      </button>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.title}
      >
        {mode === "new" ? "Nuevo Proyecto" : "Editar Proyecto"}
      </motion.h1>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Título */}
        <div className={styles.inputGroup}>
          <label>Título</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        {/* Short Desc */}
        <div className={styles.inputGroup}>
          <label>Descripción corta</label>
          <input
            type="text"
            value={form.shortDesc}
            onChange={(e) => setForm({ ...form, shortDesc: e.target.value })}
            required
          />
        </div>

        {/* Imagen principal */}
        <div className={styles.inputGroup}>
          <label>Imagen principal (URL)</label>
          <input
            type="text"
            value={form.img}
            onChange={(e) => setForm({ ...form, img: e.target.value })}
            required
          />
        </div>

        {/* Imágenes adicionales */}
        <div className={styles.inputGroup}>
          <label>Imágenes adicionales</label>
          {form.images.map((img, i) => (
            <div key={i} className={styles.imageField}>
              <input
                type="text"
                value={img}
                onChange={(e) => {
                  const newImages = [...form.images];
                  newImages[i] = e.target.value;
                  setForm({ ...form, images: newImages });
                }}
              />
              {form.images.length > 1 && (
                <button type="button" onClick={() => removeImageField(i)} className={styles.removeImg}>
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addImageField} className={styles.addImgBtn}>
            <Plus size={18} />
            Agregar imagen
          </button>
        </div>

        {/* Tecnologías */}
        <div className={styles.inputGroup}>
          <label>Tecnologías (separadas por coma)</label>
          <input
            type="text"
            value={form.languages.join(", ")}
            onChange={(e) => setForm({ ...form, languages: e.target.value.split(",").map(t => t.trim()) })}
            required
          />
        </div>

        {/* Links */}
        <div className={styles.linksGrid}>
          <div className={styles.inputGroup}>
            <label>GitHub</label>
            <input
              type="url"
              value={form.github}
              onChange={(e) => setForm({ ...form, github: e.target.value })}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>Demo</label>
            <input
              type="url"
              value={form.demo}
              onChange={(e) => setForm({ ...form, demo: e.target.value })}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>Video</label>
            <input
              type="url"
              value={form.video}
              onChange={(e) => setForm({ ...form, video: e.target.value })}
            />
          </div>
        </div>

        {/* Descripción completa */}
        <div className={styles.inputGroup}>
          <label>Descripción completa (HTML permitido)</label>
          <textarea
            rows={10}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            required
            className={styles.textarea}
          />
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          className={styles.submitBtn}
        >
          {mode === "new" ? "Crear Proyecto" : "Guardar Cambios"}
        </motion.button>
      </form>
    </div>
  );
}